
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { Message } from "./types";

export const useTeamMessages = (teamId?: string) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useSupabaseUser();

  // Function to remove a message from the messages array
  const removeMessage = (messageId: string) => {
    setMessages(prev => prev.filter(message => message.id !== messageId));
  };

  // Function to clear unread count when user opens the chat
  const clearUnreadCount = () => {
    if (teamId) {
      localStorage.setItem(`team_${teamId}_last_read`, new Date().toISOString());
      setUnreadCount(0);
    }
  };
  
  // Function to get the last read timestamp for a team
  const getLastReadTimestamp = (teamId: string) => {
    const lastRead = localStorage.getItem(`team_${teamId}_last_read`);
    return lastRead || '2000-01-01T00:00:00.000Z'; // Default old date if never read
  };

  useEffect(() => {
    if (!user) return;
    
    const checkUnreadMessages = async () => {
      try {
        // If a specific team ID is provided, check unread messages for that team
        if (teamId) {
          const lastRead = getLastReadTimestamp(teamId);
          
          // Query for unread messages
          const { count, error } = await supabase
            .from('team_messages')
            .select('*', { count: 'exact', head: true })
            .eq('team_id', teamId)
            .gt('created_at', lastRead)
            .neq('author_id', user.id); // Don't count user's own messages
            
          if (error) {
            console.error('Error fetching unread count:', error);
            return;
          }
          
          setUnreadCount(count || 0);
          console.log(`Unread count for team ${teamId}: ${count || 0}`);
        } 
        // If no team ID provided, get total unread count across all teams
        else {
          // Get all teams the user is a member of
          const { data: userTeams, error: teamError } = await supabase.rpc('get_user_team_ids', {
            user_id: user.id
          });
          
          if (teamError) {
            console.error('Error fetching user teams:', teamError);
            return;
          }
          
          // Calculate total unread count across all teams
          let totalUnread = 0;
          
          for (const { team_id } of userTeams || []) {
            const lastRead = getLastReadTimestamp(team_id);
            
            const { count, error } = await supabase
              .from('team_messages')
              .select('*', { count: 'exact', head: true })
              .eq('team_id', team_id)
              .gt('created_at', lastRead)
              .neq('author_id', user.id);
              
            if (error) {
              console.error(`Error fetching unread count for team ${team_id}:`, error);
              continue;
            }
            
            totalUnread += (count || 0);
            console.log(`Unread count for team ${team_id}: ${count || 0}`);
          }
          
          setUnreadCount(totalUnread);
        }
      } catch (err) {
        console.error('Error in checkUnreadMessages:', err);
      }
    };
    
    // Initial check
    checkUnreadMessages();
    
    // Only set up team-specific subscription if teamId is provided
    if (!teamId) {
      // For global subscription, we'll just recheck counts periodically
      const interval = setInterval(checkUnreadMessages, 60000);
      return () => clearInterval(interval);
    }
    
    // Single team-specific subscription for unread count updates
    const unreadSubscription = supabase
      .channel(`team_unread_${teamId}`, {
        config: {
          broadcast: { self: false }
        }
      })
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'team_messages',
          filter: `team_id=eq.${teamId}`
        }, 
        (payload) => {
          // Only increment unread count if the message is not from the current user
          if (payload.new.author_id !== user.id) {
            setUnreadCount(prev => prev + 1);
          }
        }
      )
      .subscribe((status, err) => {
        console.log(`Unread subscription status for team ${teamId}:`, status);
        if (err) {
          console.error('Unread subscription error:', err);
        }
      });
    
    // Cleanup
    return () => {
      if (unreadSubscription) {
        unreadSubscription.unsubscribe();
      }
    };
  }, [teamId, user]);

  // Check if user is an admin for this team
  useEffect(() => {
    if (!user || !teamId) return;

    const checkAdminStatus = async () => {
      try {
        // Use maybeSingle() instead of single() to handle the case when no record is found
        const { data, error } = await supabase
          .from('team_members')
          .select('role')
          .eq('team_id', teamId)
          .eq('profile_id', user.id)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          console.error('Error checking admin status:', error);
          return;
        }

        const isUserAdmin = data?.role === 'admin';
        setIsAdmin(isUserAdmin);
        console.log("Admin status in useTeamMessages:", isUserAdmin);
      } catch (err) {
        console.error('Error in checkAdminStatus:', err);
      }
    };

    checkAdminStatus();
  }, [teamId, user]);

  // Fetch messages for the team
  useEffect(() => {
    if (!teamId || !user) {
      console.log('useTeamMessages: Skipping fetch - missing teamId or user', { teamId, userId: user?.id });
      return;
    }

    console.log('useTeamMessages: Starting message fetch for team:', teamId);

    const fetchMessages = async () => {
      setIsLoading(true);
      try {
        console.log('useTeamMessages: Querying team_messages table for team:', teamId);
        const { data, error } = await supabase
          .from('team_messages')
          .select(`
            id, 
            content, 
            created_at,
            updated_at,
            image_url,
            author_id
          `)
          .eq('team_id', teamId)
          .order('created_at', { ascending: true });
        
        console.log('useTeamMessages: Query completed. Error:', error, 'Data count:', data?.length);

        if (error) {
          console.error('useTeamMessages: Error fetching messages:', error);
          return;
        }

        console.log('useTeamMessages: Raw data received:', data?.length || 0, 'messages');

        // Fetch author profiles for all messages
        const authorIds = [...new Set(data.map(m => m.author_id))];
        console.log('useTeamMessages: Fetching profiles for author IDs:', authorIds);
        
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', authorIds);

        console.log('useTeamMessages: Profiles fetched:', profiles?.length, 'Error:', profilesError);

        // Create a map of author_id to profile
        const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

        // Transform the data to match the Message type
        const formattedMessages = data.map(message => ({
          id: message.id,
          content: message.content,
          created_at: message.created_at,
          updated_at: message.updated_at,
          image_url: message.image_url,
          author: profileMap.get(message.author_id) ? {
            id: message.author_id,
            full_name: profileMap.get(message.author_id)!.full_name
          } : {
            id: message.author_id,
            full_name: 'Unknown User'
          }
        }));

        console.log('useTeamMessages: Formatted messages:', formattedMessages);
        console.log('useTeamMessages: About to set messages state with', formattedMessages.length, 'messages');
        setMessages(formattedMessages);
        console.log('useTeamMessages: setMessages called');
      } catch (err) {
        console.error('useTeamMessages: Exception in fetchMessages:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMessages();

    // Set up subscription for real-time updates with better error handling and reconnection
    const messageSubscription = supabase
      .channel(`team_messages_${teamId}`, {
        config: {
          broadcast: { self: false },
          presence: { key: `user_${user?.id}` }
        }
      })
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'team_messages',
          filter: `team_id=eq.${teamId}`
        }, 
        async (payload) => {
          console.log('Real-time INSERT received:', payload);
          
          // Fetch the author info for the new message
          const { data, error } = await supabase
            .from('profiles')
            .select('id, full_name')
            .eq('id', payload.new.author_id)
            .maybeSingle();

          if (error && error.code !== 'PGRST116') {
            console.error('Error fetching author details:', error);
          }

          const newMessage: Message = {
            id: payload.new.id,
            content: payload.new.content,
            created_at: payload.new.created_at,
            image_url: payload.new.image_url,
            author: data ? {
              id: data.id,
              full_name: data.full_name
            } : {
              id: payload.new.author_id,
              full_name: 'Unknown User'
            }
          };

          console.log('Adding new message to state:', newMessage);
          setMessages(prev => {
            // Check if message already exists to prevent duplicates
            const messageExists = prev.some(msg => msg.id === newMessage.id);
            if (messageExists) {
              console.log('Message already exists, skipping duplicate');
              return prev;
            }
            // Add message and sort to ensure proper order
            const updatedMessages = [...prev, newMessage].sort((a, b) => 
              new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            );
            return updatedMessages;
          });
        }
      )
      .on('postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'team_messages',
          filter: `team_id=eq.${teamId}`
        },
        (payload) => {
          console.log('Real-time UPDATE received:', payload);
          setMessages(prev => prev.map(msg => 
            msg.id === payload.new.id 
              ? { 
                  ...msg, 
                  content: payload.new.content,
                  updated_at: payload.new.updated_at,
                  created_at: payload.new.created_at 
                }
              : msg
          ));
        }
      )
      .on('postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'team_messages',
          filter: `team_id=eq.${teamId}`
        },
        (payload) => {
          console.log('Real-time DELETE received:', payload);
          setMessages(prev => prev.filter(msg => msg.id !== payload.old.id));
        }
      )
      .subscribe((status, err) => {
        console.log(`🔴 REALTIME SUBSCRIPTION STATUS for team ${teamId}:`, status);
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to team_messages channel');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Channel subscription error');
        } else if (status === 'TIMED_OUT') {
          console.error('⏱️ Channel subscription timed out');
        } else if (status === 'CLOSED') {
          console.error('🔒 Channel subscription closed');
        }
        if (err) {
          console.error('useTeamMessages: Subscription error:', err);
        }
      });

    return () => {
      console.log(`Cleaning up subscription for team ${teamId}`);
      messageSubscription.unsubscribe();
    };
  }, [teamId, user]);

  // Add event listener for optimistic updates
  useEffect(() => {
    const handleNewMessage = (event: CustomEvent) => {
      if (event.detail.teamId === teamId) {
        setMessages(prev => {
          const messageExists = prev.some(msg => msg.id === event.detail.message.id);
          if (messageExists) return prev;
          
          return [...prev, event.detail.message].sort((a, b) => 
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        });
      }
    };

    window.addEventListener('newTeamMessage', handleNewMessage as EventListener);
    return () => {
      window.removeEventListener('newTeamMessage', handleNewMessage as EventListener);
    };
  }, [teamId]);

  return { 
    unreadCount, 
    clearUnreadCount, 
    messages, 
    isAdmin, 
    isLoading,
    setIsLoading,
    removeMessage
  };
};
